import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReCAPTCHA from "react-google-recaptcha";
import secureStorage from "../utils/secureStorage";
import { useRef } from "react";

// Error Boundary to catch any uncaught internal reCAPTCHA errors (e.g. Timeout)
class RecaptchaErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        console.error("ReCAPTCHA internal error:", error, info);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="p-4 mb-4 bg-yellow-100 text-yellow-800 rounded">
                    ⚠️ reCAPTCHA failed to initialize. Please refresh the page.
                </div>
            );
        }
        return this.props.children;
    }
}

const generateCaptcha = () => {
    const a = Math.floor(Math.random() * 10);
    const b = Math.floor(Math.random() * 10);
    return {
        question: `${a} + ${b} = ?`,
        answer: (a + b).toString(),
    };
};

const LoginPage = () => {
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [captcha] = useState(generateCaptcha()); // your math captcha (unused)
    const [captchaInput, setCaptchaInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const [captchaLoadError, setCaptchaLoadError] = useState(false);
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;
    const recaptchaRef = useRef(null);

    // Refresh your own math captcha if ever used
    const refreshCaptcha = () => {
        // setCaptcha(generateCaptcha()); // no-op since captcha state is readonly here
        setCaptchaInput("");
    };

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const emailParam = queryParams.get("email");
        if (emailParam) {
            setEmail(emailParam);
        }
        
        refreshCaptcha();
        // detect if <script> load failed
        if (window.recaptchaLoadFailed) {
            setCaptchaLoadError(true);
        }
    }, [location.search]);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (captchaLoadError) {
            toast.error("CAPTCHA isn’t available right now. Try again later.", {
                position: "top-right",
                autoClose: 3000,
            });
            return;
        }
        if (!recaptchaToken) {
            toast.error("Please complete the captcha.", {
                position: "top-right",
                autoClose: 3000,
            });
            return;
        }

        setLoading(true);

        const apiUrl = `${API_URL}login/admin?id=${encodeURIComponent(
            email
        )}&password=${encodeURIComponent(password)}&recaptchaToken=${recaptchaToken}`;

        try {
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-HIP-ID": "healthidhub",
                },
            });

            if (response.ok) {
                const result = await response.json();
                secureStorage.set("authToken", result.data.token);
                secureStorage.set("clinic", result.data.hipId);
                secureStorage.set("user", result.data);
                secureStorage.set("orgId", result.data.organizationId);
                secureStorage.set("orgName", result.data.organizationName);
                secureStorage.set("role", "HOSPITAL");

                document.title = result.data.organizationName || "HealthIdHub";

                toast.success("Login Successful!", {
                    position: "top-right",
                    autoClose: 3000,
                });
                navigate("/hospital-dashboard");
            } else {
                const error = await response.json();
                toast.error(error.message || "Invalid email or password!", {
                    position: "top-right",
                    autoClose: 3000,
                });
                if (recaptchaRef.current) recaptchaRef.current.reset();
                setRecaptchaToken(null);
            }
        } catch (err) {
            if (recaptchaRef.current) recaptchaRef.current.reset();
            setRecaptchaToken(null);

            toast.error("An error occurred. Please try again later.", {
                position: "top-right",
                autoClose: 3000,
            });

        } finally {
            setLoading(false);
            setRecaptchaToken(null);
        }
    };

    return (
        <div className="flex h-screen">
            {/* Left Section */}
            <div
                className="w-1/2 flex flex-col items-center justify-center"
                style={{
                    background:
                        "linear-gradient(180deg, #92D7D1 0%, #0F766E 84.79%, #0F766E 100%)",
                }}
            >
                <div className="text-center px-12">
                    <h1 className="text-4xl font-bold text-white mb-4">{secureStorage.get("orgName") || "HealthIdHub"}</h1>
                    <p className="text-white text-lg leading-relaxed">
                        Secure your health journey with our digital platform. Manage your
                        records, access personalized care, and connect with trusted
                        providers.
                    </p>
                </div>
            </div>

            {/* Right Section */}
            <div className="w-1/2 bg-white flex items-center justify-center">
                <div className="max-w-md w-full px-12">
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome</h2>
                    <p className="text-gray-600 mb-8">Login To Your Vendor Account</p>

                    <form onSubmit={handleLogin}>
                        {/* Email */}
                        <div className="mb-4">
                            <input
                                type="email"
                                placeholder="Email Address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full pl-4 pr-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-teal-500"
                                required
                            />
                        </div>

                        {/* Password */}
                        <div className="mb-4">
                            <input
                                type="password"
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full pl-4 pr-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-teal-500"
                                required
                            />
                        </div>

                        {/* reCAPTCHA */}
                        <div className="mb-6">
                            <RecaptchaErrorBoundary>
                                {captchaLoadError ? (
                                    <div className="text-red-500">
                                        reCAPTCHA failed to load; please refresh.
                                    </div>
                                ) : (
                                    <ReCAPTCHA
                                        ref={recaptchaRef}
                                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                        onChange={(token) => setRecaptchaToken(token)}
                                        onExpired={() => setRecaptchaToken(null)}
                                        onErrored={() =>
                                            toast.error(
                                                "reCAPTCHA failed to load. Please check your connection."
                                            )
                                        }
                                    />
                                )}
                            </RecaptchaErrorBoundary>
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full flex items-center justify-center bg-teal-500 text-white py-3 rounded-full hover:bg-teal-600 transition duration-300 ${loading ? "opacity-70 cursor-not-allowed" : ""
                                }`}
                        >
                            {loading && (
                                <svg
                                    className="animate-spin h-5 w-5 mr-2 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                    />
                                </svg>
                            )}
                            {loading ? "Logging In..." : "Log In"}
                        </button>
                    </form>
                </div>
            </div>

            <ToastContainer />
        </div>
    );
};

export default LoginPage;
